/**
 * Message component
 *
 * Renders a single chat message with appropriate styling based on role.
 * Includes copy and feedback (thumbs up/down) actions for assistant messages.
 */

import { useState, useCallback } from 'react';
import type { Message as MessageType } from '../../types';

interface MessageProps {
  message: MessageType;
  feedback?: 'up' | 'down' | null;
  onFeedback?: (messageId: number, type: 'up' | 'down') => void;
}

export function Message({ message, feedback, onFeedback }: MessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const [copied, setCopied] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // Parse markdown-like formatting
  const formatContent = (content: string) => {
    // Simple markdown rendering (could use react-markdown for full support)
    const lines = content.split('\n');
    return lines.map((line, i) => (
      <span key={i}>
        {line}
        {i < lines.length - 1 && <br />}
      </span>
    ));
  };

  const handleCopy = useCallback(async () => {
    if (!message.content) return;

    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [message.content]);

  const handleFeedback = useCallback((type: 'up' | 'down') => {
    if (!message.id || !onFeedback) return;
    onFeedback(message.id, type);
  }, [message.id, onFeedback]);

  return (
    <div
      className={`message ${isUser ? 'user' : 'assistant'}`}
      data-testid={isUser ? 'user-message' : 'assistant-message'}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="message-content">
        {formatContent(message.content || '')}
      </div>

      {/* Action buttons for assistant messages */}
      {isAssistant && message.content && (
        <div className={`message-actions ${showActions || feedback ? 'visible' : ''}`}>
          {/* Copy button */}
          <button
            className="action-btn"
            onClick={handleCopy}
            title={copied ? 'Kopirano!' : 'Kopiraj'}
            aria-label={copied ? 'Kopirano' : 'Kopiraj tekst'}
          >
            {copied ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            )}
          </button>

          {/* Thumbs up */}
          <button
            className={`action-btn ${feedback === 'up' ? 'active' : ''}`}
            onClick={() => handleFeedback('up')}
            title="Dobar odgovor"
            aria-label="Dobar odgovor"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill={feedback === 'up' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
            </svg>
          </button>

          {/* Thumbs down */}
          <button
            className={`action-btn ${feedback === 'down' ? 'active' : ''}`}
            onClick={() => handleFeedback('down')}
            title="Los odgovor"
            aria-label="Los odgovor"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill={feedback === 'down' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default Message;
